import { useEffect, useRef } from 'react'

import { ProjectsTrackerPage } from './ProjectTrackerPage'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { CameraPlusIcons } from '@/components/icons/CameraPlusIcon'
import { useListPageViews } from '@/services/queries/views'
import { useLocation } from 'wouter'
import { usePDFifyStore } from '@/components/pdf/state'
import { useCheckImageDataNeedsUpdate } from '@/utils/hooks/useCheckImageDataNeedsUpdate'
import {
  DocumentContainer,
  InnerDocumentContainer,
} from '../../DocumentContainer'
import {
  MAIN_DOCUMENT_CONTAINER_TOP_PADDING,
  useAppStore,
  useDocumentState,
} from '@/state'

export const ProjectTracker = () => {
  const { isLoading, data } = useDocumentPageQuery()
  const { isLoading: isLoadingViews, data: viewsData } = useListPageViews({
    cadVersionId: data?.version?.id,
    documentVersionId: data?.documentVersion?.id as string,
  })

  const isGeneratingPdf = usePDFifyStore((state) => state.isGenerating)
  const documentRef = useRef<HTMLDivElement>(null)
  const documentContainerRef = useRef<HTMLDivElement>(null)
  const firstDocRef = useRef<HTMLDivElement>(null)
  const defaultZoom = useDocumentState((state) => state.defaultZoom)
  const setDefaultZoom = useDocumentState((state) => state.setDefaultZoom)
  const isUserReadOnly = useAppStore((state) => state.isUserReadOnly())

  const [, setLocation] = useLocation()

  useEffect(() => {
    if (documentContainerRef.current && firstDocRef.current && !defaultZoom) {
      const { height: containerHeight, width: containerWidth } =
        documentContainerRef.current.getBoundingClientRect()

      const { height: firstDocHeight, width: firstDocWidth } =
        firstDocRef.current.getBoundingClientRect()

      const widthRatio = Math.abs(containerWidth / firstDocWidth - 1.05)
      const heightRatio = Math.abs(
        (containerHeight - MAIN_DOCUMENT_CONTAINER_TOP_PADDING) /
          firstDocHeight -
          1.05,
      )

      setDefaultZoom(Math.round(Math.min(widthRatio, heightRatio) * 100))
    }
  }, [documentContainerRef, firstDocRef, isLoading, isLoadingViews])

  useCheckImageDataNeedsUpdate({
    views: viewsData || [],
  })

  if (isLoading || isLoadingViews) {
    return <Spinner />
  }

  const pages = data?.documentPages || []
  const projectId = data?.project.id || ''
  const documentId = data?.documentId || ''
  const isLatestDocumentVersion = data?.isLatestDocumentVersion

  return (
    <DocumentContainer ref={documentContainerRef}>
      <InnerDocumentContainer ref={documentRef}>
        {pages.length === 0 && (
          <div className="mt-20 flex flex-col justify-center space-y-2">
            <div className="text-md">
              Capture a smart view to start tracking your project
            </div>
            <Button
              className="w-3/6 flex self-center"
              onClick={() => {
                setLocation(`/p/${projectId}/document/${documentId}/cad`)
              }}
            >
              <div className="flex items-center space-x-2">
                <CameraPlusIcons className="h-6 w-6 relative left-px stroke-white" />
                <span>Capture View</span>
              </div>
            </Button>
          </div>
        )}
        {pages.length > 0 &&
          pages.map((page, i) => {
            return (
              <div
                key={page.id}
                ref={i === 0 ? firstDocRef : undefined}
                className="print:w-full"
              >
                <ProjectsTrackerPage
                  pageId={String(page.id)}
                  pageTitle={page.name}
                  pageCreatedAt={page.created}
                  documentPage={page as any}
                  isReadOnly={
                    isGeneratingPdf ||
                    !isLatestDocumentVersion ||
                    isUserReadOnly
                  }
                />
              </div>
            )
          })}
      </InnerDocumentContainer>
    </DocumentContainer>
  )
}
